import NextLink from 'next/link'
import { useRouter } from 'next/router'
import React from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import styled from 'styled-components'
import { Brand } from 'types/graphql'
import { resizedImageUrl } from 'utils/imageUrl'
import { media } from 'utils/media'
import { Link } from 'lib/Link'
import { LoadingLine } from 'lib/LoadingLine'
import { Text } from 'lib/Text'
import { StarSelector } from 'modules/reviews/form/components/StarSelector'
import { DismissBrand } from './DismissBrand'

const Row = styled.article`
  align-items: center;
  display: flex;
  gap: 24px;
`

const BrandImage = styled.a`
  background-color: var(--black-10);
  background-size: cover;
  background-position: center center;
  border: 2px solid var(--black-100);
  height: 80px;
  width: 80px;
  display: block;

  ${media.tinyPhoneDown} {
    display: none;
  }
`

const StyledLink = styled(Link)`
  font-size: var(--text-size-p);
  line-height: var(--line-height-p);
  font-family: var(--monument-grotesk-medium);
  font-weight: normal;

  &,
  :hover {
    border: none;
  }

  @media (hover: none) {
    pointer-events: none;
  }
`

const FormContainer = styled.div`
  margin-top: 8px;
  width: 100%;
`

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
`

interface Props {
  brand: Pick<Brand, 'id' | 'primaryImageFullPath' | 'slug' | 'title'>
  linkToBrandPage?: boolean
  onDismissClick?: () => void
}

export const RatableBrand = ({ brand, linkToBrandPage = false, onDismissClick }: Props) => {
  const formMethods = useForm()
  const { push } = useRouter()
  const handleRatingChange = (rating: number, brandSlug: string) => {
    push(`/brands/${brandSlug}/review?stars=${rating}&from=homepage-suggestion`)
    return
  }

  return (
    <Row key={brand.id}>
      {linkToBrandPage ? (
        <NextLink href={`/brands/${brand.slug}`} passHref prefetch={false}>
          <BrandImage
            aria-label={`${brand.title} link`}
            style={{ backgroundImage: `url(${resizedImageUrl(brand.primaryImageFullPath, { width: 160, height: 160 })})` }}
          />
        </NextLink>
      ) : (
        <BrandImage
          aria-label={`${brand.title} link`}
          style={{ backgroundImage: `url(${resizedImageUrl(brand.primaryImageFullPath, { width: 160, height: 160 })})` }}
        />
      )}
      <FormContainer>
        <FormProvider {...formMethods}>
          <TitleRow>
            {linkToBrandPage ? (
              <StyledLink aria-label={`${brand.title} link`} href={`/brands/${brand.slug}`}>
                {brand.title}
              </StyledLink>
            ) : (
              <Text weight="semi">{brand.title}</Text>
            )}
            <DismissBrand brandId={brand.id} onClick={onDismissClick} />
          </TitleRow>
          <StarSelector size="small" onRatingChange={(rating) => handleRatingChange(rating, brand.slug)} showHint={false} />
        </FormProvider>
      </FormContainer>
    </Row>
  )
}

export const RatableBrandPlaceholder = () => {
  const formMethods = useForm()

  return (
    <Row key="placeholder">
      <BrandImage aria-label="Placeholder link" style={{ backgroundColor: 'var(--black-20)', border: 0 }} />
      <FormContainer>
        <FormProvider {...formMethods}>
          <Text weight="semi">
            <LoadingLine height={22} width={100} />
          </Text>
          <StarSelector disabled size="small" showHint={false} />
        </FormProvider>
      </FormContainer>
    </Row>
  )
}
